
import { supabase } from "@/integrations/supabase/client";
import { DetectedLocation, DetectedMultipleLocations } from '../mappings/LocationMapping';
import { DetectedFacilities } from '../filters/FacilitiesFilter';
import { applyLocationFilter } from '../filters/LocationFilter';
import { applyPriceFilter } from '../filters/PriceFilter';
import { applyFacilitiesFilter } from '../filters/FacilitiesFilter';

export const verifySearchWithSample = async (
  detectedLocation: DetectedLocation | DetectedMultipleLocations,
  priceConstraints: any,
  detectedFacilities: DetectedFacilities | null
) => {
  console.log('🔍 === STEP 8: VERIFYING HIERARCHY WITH SAMPLE ===');
  
  // Skip verification for multiple locations for now
  if ('locations' in detectedLocation) {
    console.log('⏭️ Skipping verification sample for multiple locations');
    return;
  }
  
  const testQuery = supabase
    .from('Complete feed')
    .select('name, country, city, region, price, facilities, description')
    .not('imageURL', 'is', null)
    .not('name', 'is', null);
  
  let testQueryWithLocation = applyLocationFilter(testQuery, detectedLocation);
  if (priceConstraints) {
    testQueryWithLocation = applyPriceFilter(testQueryWithLocation, priceConstraints);
  }
  if (detectedFacilities) {
    testQueryWithLocation = applyFacilitiesFilter(testQueryWithLocation, detectedFacilities);
  }
  
  const { data: testData, error: testError } = await testQueryWithLocation.limit(10);
  
  if (testError) {
    console.error('❌ Test query error:', testError);
    return;
  }
  
  if (!testData) return;
  
  console.log('🔍 Sample results after all filters:');
  testData.forEach((result, index) => {
    console.log(`  ${index + 1}. "${result.name}" - ${result.city}, ${result.region}, ${result.country} - €${result.price || 'N/A'}`);
    if (detectedFacilities) {
      console.log(`     Facilities: ${result.facilities || 'N/A'}`);
    }
  });
  
  // Verify price filtering
  if (priceConstraints) {
    console.log('💰 === PRICE FILTER VERIFICATION ===');
    if (priceConstraints.maxPrice) {
      const overPriced = testData.filter(r => r.price && r.price > priceConstraints.maxPrice);
      if (overPriced.length > 0) {
        console.error('❌❌❌ PRICE FILTER ERROR: Some results exceed maxPrice!');
        overPriced.forEach(r => {
          console.error(`  - "${r.name}" costs €${r.price} but max should be €${priceConstraints.maxPrice}`);
        });
      } else {
        console.log('✅✅✅ All results are within maxPrice limit');
      }
    }
    
    if (priceConstraints.minPrice) {
      const underPriced = testData.filter(r => r.price && r.price < priceConstraints.minPrice);
      if (underPriced.length > 0) {
        console.error('❌❌❌ PRICE FILTER ERROR: Some results are below minPrice!');
        underPriced.forEach(r => {
          console.error(`  - "${r.name}" costs €${r.price} but min should be €${priceConstraints.minPrice}`);
        });
      } else {
        console.log('✅✅✅ All results are above minPrice limit');
      }
    }
  }
  
  // Verify hierarchy is more flexible - accept actual database values
  if ('info' in detectedLocation && detectedLocation.type === 'city') {
    const expectedCity = detectedLocation.info.city;
    const expectedRegion = detectedLocation.info.region;
    const expectedCountry = detectedLocation.info.country;
    
    console.log('🎯 Expected vs Actual comparison:');
    console.log(`  Expected: ${expectedCity}, ${expectedRegion}, ${expectedCountry}`);
    
    // More flexible validation - check if results are related to the search location
    const wrongResults = testData.filter(r => {
      // Must be correct country
      if (r.country !== expectedCountry) return true;
      
      // For city, be flexible - check if any field contains the city name
      const cityMatch = 
        r.city?.toLowerCase().includes(expectedCity.toLowerCase()) ||
        r.region?.toLowerCase().includes(expectedCity.toLowerCase()) ||
        r.name?.toLowerCase().includes(expectedCity.toLowerCase());
      
      return !cityMatch;
    });
    
    if (wrongResults.length > 0) {
      console.warn('⚠️ Some results might be outside the specified city (but this is often expected):');
      wrongResults.forEach(r => {
        console.warn(`  - "${r.name}" is in "${r.city}, ${r.region}, ${r.country}"`);
      });
    } else {
      console.log('✅✅✅ All sample results are related to the correct location');
    }
    
    // Log actual database structure for debugging
    console.log('📊 Actual database regions found:');
    const uniqueRegions = [...new Set(testData.map(r => r.region))];
    console.log('  Regions:', uniqueRegions);
  }
};
